/**
 * sl-yphr.18: DatabaseFullscreenView
 *
 * Self-contained fullscreen view for database blocks.
 * Registered via FullscreenRegistry; rendered by FullscreenModal.
 *
 * Manages: URL sync, localStorage persistence, tab order, view sync,
 * custom theme override, tab context menu, tab add/switch.
 */

import { useConfirmModal } from '@affine/component';
import { Menu, MenuItem, MenuSeparator } from '@affine/component/ui/menu';
import { useGuard } from '@affine/core/components/guard';
import { EditorService } from '@affine/core/modules/editor';
import { isPresetTheme } from '@affine/core/modules/theme-presets';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { DeleteIcon, EditIcon } from '@blocksuite/icons/rc';
import {
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
} from '@streamlain/database-identity';
import { useFramework, useLiveData, useService } from '@toeverything/infra';
import { useTheme } from 'next-themes';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { LitDatabaseFullscreenEditor } from '../../../blocksuite/editors';
import {
  isDatabaseCellEditingKeyboardEventTarget,
  isEditableKeyboardEventTarget,
} from '../../../blocksuite/utils/keyboard-event-target';
import { getFullscreenDatabaseSpecs } from '../../../blocksuite/view-extensions/database-fullscreen/specs';
import { AuthService } from '../../cloud';
import {
  prioritizeDatabaseRowShardForFocus,
  prioritizeDatabaseSubdocsForBlock,
} from '../../doc/services/database-subdoc-preload';
import { WorkbenchService } from '../../workbench';
import { useWorkspaceCustomApps } from '../hooks/use-workspace-custom-apps';
import { DocViewStateService } from '../services/doc-view-state';
import type { FullscreenViewProps } from '../services/fullscreen-registry';
import { FullscreenRegistry } from '../services/fullscreen-registry';
import {
  describeManagedDatabaseOwner,
  isManagedDatabaseProtected,
  ManagedDatabaseDeleteDialog,
  type ManagedDatabaseProtectionInput,
  readManagedDatabaseProtectionFromModel,
  resolveCustomAppDatabaseOwner,
} from './components/managed-database-delete-dialog';

interface DatabaseFocusParams {
  columnId: string | null;
  rowId: string | null;
  rowShardDocId: string | null;
}

function readDatabaseFocusParams(search: string): DatabaseFocusParams {
  try {
    const params = new URLSearchParams(search);
    return {
      columnId: params.get('fullscreenDatabaseColumnId'),
      rowId: params.get('fullscreenDatabaseRowId'),
      rowShardDocId: params.get('fullscreenDatabaseRowShardDocId'),
    };
  } catch {
    return { columnId: null, rowId: null, rowShardDocId: null };
  }
}

function isUndoRedoKeyboardShortcut(event: KeyboardEvent): boolean {
  return (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z';
}

export const DatabaseFullscreenView: FC<FullscreenViewProps> = ({
  blockId,
}) => {
  const editor = useService(EditorService).editor;
  const registry = useService(FullscreenRegistry);
  const docViewState = useService(DocViewStateService);
  const workspaceService = useService(WorkspaceService);
  const workbenchService = useService(WorkbenchService);
  const workbenchLocation = useLiveData(workbenchService.workbench.location$);
  const authService = useService(AuthService);
  const framework = useFramework();
  const currentAccount = useLiveData(authService.session.account$);
  const docId = editor.doc.id;
  const page = editor.doc.blockSuiteDoc;
  const canViewCustomApps = useGuard('Workspace_CustomApps_View') === true;
  const { views: customApps } = useWorkspaceCustomApps(
    canViewCustomApps ? workspaceService.workspace.id : null
  );

  // Ref for latest docId — used by unmount cleanup to clear correct localStorage key
  const docIdRef = useRef(docId);
  docIdRef.current = docId;
  const fullscreenEditorHostRef = useRef<HTMLDivElement | null>(null);

  // Fullscreen database specs
  const fullscreenDatabaseSpecs = useMemo(
    () => getFullscreenDatabaseSpecs(framework),
    [framework]
  );

  // Custom theme detection
  const { resolvedTheme } = useTheme();
  const isCustomTheme = resolvedTheme ? isPresetTheme(resolvedTheme) : false;

  // View ID (database tab) — read from URL > DocViewState > document state.
  const [viewId, setViewId] = useState<string | null>(() => {
    // 1. URL has highest priority (F5 reload)
    try {
      const url = new URLSearchParams(window.location.search);
      const urlViewId = url.get('fullscreenViewId');
      if (urlViewId) return urlViewId;
    } catch {
      /* ignore */
    }
    // 2. DocViewState has second priority (sidebar navigation return)
    const savedState = docViewState.get(docId);
    if (savedState?.fullscreen?.databaseViewId) {
      return savedState.fullscreen.databaseViewId;
    }
    return null;
  });
  const focusCellParams = useMemo(
    () => readDatabaseFocusParams(workbenchLocation.search),
    [workbenchLocation.search]
  );
  const focusRequestKey = focusCellParams.rowId
    ? `${blockId}:${focusCellParams.rowId}:${focusCellParams.rowShardDocId ?? ''}:${focusCellParams.columnId ?? ''}`
    : null;
  const [blockAvailable, setBlockAvailable] = useState(
    () => blockId === '__placeholder__' || !!page.getBlock(blockId)
  );

  useEffect(() => {
    if (blockId === '__placeholder__' || !blockAvailable) return;
    return prioritizeDatabaseSubdocsForBlock(page, blockId, workspaceService);
  }, [blockAvailable, blockId, page, workspaceService]);

  useEffect(() => {
    if (
      blockId === '__placeholder__' ||
      !blockAvailable ||
      !focusRequestKey ||
      !focusCellParams.rowShardDocId
    ) {
      return;
    }

    return prioritizeDatabaseRowShardForFocus(
      page,
      blockId,
      focusCellParams.rowShardDocId,
      workspaceService,
      () => {
        // The fullscreen editor already owns the semantic focus request and
        // retries it while the authoritative row shard hydrates. This callback
        // intentionally only keeps the preload alive; gating focus props on it
        // deadlocks the editor out of requesting/observing the target shard.
      }
    );
  }, [
    blockAvailable,
    blockId,
    focusCellParams.rowShardDocId,
    focusRequestKey,
    page,
    workspaceService,
  ]);

  useEffect(() => {
    const refresh = () => {
      setBlockAvailable(
        blockId === '__placeholder__' || !!page.getBlock(blockId)
      );
    };

    refresh();
    const rootSub = page.slots.rootAdded.subscribe(refresh);
    const blockSub = page.slots.blockUpdated.subscribe(({ id }) => {
      if (id === blockId || !page.getBlock(blockId)) {
        refresh();
      }
    });

    return () => {
      rootSub.unsubscribe();
      blockSub.unsubscribe();
    };
  }, [blockId, page]);

  // Tab order persistence
  const [savedTabOrder, setSavedTabOrder] = useState<string[]>(() => {
    const meta =
      workspaceService.workspace.docCollection.meta.getDocMeta(docId);
    return (
      (meta as { databaseTabOrder?: string[] } | undefined)?.databaseTabOrder ??
      []
    );
  });

  // Tab context menu state
  const [tabContextMenu, setTabContextMenu] = useState<{
    tabId: string;
    tabTitle: string;
    titleLoading?: boolean;
    x: number;
    y: number;
  } | null>(null);
  const [managedDelete, setManagedDelete] = useState<{
    tabId: string;
    tabTitle: string;
    protection: ManagedDatabaseProtectionInput;
  } | null>(null);
  const { openConfirmModal } = useConfirmModal();

  // ─── Undo/Redo: Cmd+Z / Cmd+Shift+Z ───
  // BlockSuite's Root KeyboardManager binds these on the editor host, but the
  // fullscreen overlay sits outside that DOM hierarchy. Forward them manually.
  useEffect(() => {
    const store = page;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isUndoRedoKeyboardShortcut(e)) return;
      const isDatabaseCellEditing = isDatabaseCellEditingKeyboardEventTarget(e);
      if (!isDatabaseCellEditing && isEditableKeyboardEventTarget(e)) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.shiftKey) {
        if (store.canRedo) store.redo();
      } else {
        if (store.canUndo) store.undo();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [page]);

  // ─── URL + localStorage sync ───
  useEffect(() => {
    // Guard: block must exist in current doc — prevents stale state leak on doc navigation.
    // When FullscreenModal persists across doc switch, this effect re-runs with the new
    // page but stale blockId. Closing the registry prevents writing stale state to the
    // new doc's URL/localStorage while preserving the OLD doc's localStorage for return.
    // Exception: '__placeholder__' is used when no database exists yet — the Lit editor
    // renders a "Create Database" placeholder UI in that case.
    if (blockId !== '__placeholder__' && !blockAvailable) {
      const savedFullscreen = docViewState.get(docId)?.fullscreen;
      if (
        savedFullscreen?.viewId === 'database' &&
        savedFullscreen.blockId === blockId
      ) {
        return;
      }
      registry.close();
      return;
    }

    // Don't persist placeholder state to URL/localStorage — it's transient
    if (blockId === '__placeholder__') return;

    editor.workbenchView?.updateQueryString(
      {
        fullscreenDatabaseId: blockId,
        fullscreenViewId: viewId ?? undefined,
      },
      { replace: true }
    );

    // Persist to DocViewStateService for cross-doc navigation restore.
    // Include the database view/tab ID so the correct tab is restored on return.
    docViewState.update(docId, {
      fullscreen: {
        viewId: 'database',
        blockId,
        databaseViewId: viewId ?? undefined,
      },
    });
  }, [blockAvailable, blockId, viewId, docId, editor, registry, docViewState]);

  useEffect(() => {
    const host = fullscreenEditorHostRef.current;
    if (!host || !focusCellParams.rowId) return;

    const handleFocusConsumed = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          columnId: string | null;
          rowId: string;
        }>
      ).detail;
      if (detail.rowId !== focusCellParams.rowId) return;
      if (
        focusCellParams.columnId &&
        detail.columnId !== focusCellParams.columnId
      ) {
        return;
      }

      editor.workbenchView?.updateQueryString(
        {
          fullscreenDatabaseColumnId: undefined,
          fullscreenDatabaseRowId: undefined,
          fullscreenDatabaseRowShardDocId: undefined,
        },
        { replace: true }
      );
    };

    host.addEventListener('database-focus-consumed', handleFocusConsumed);
    return () => {
      host.removeEventListener('database-focus-consumed', handleFocusConsumed);
    };
  }, [editor, focusCellParams.columnId, focusCellParams.rowId]);

  // ─── DocViewState cleanup on explicit user close (ESC/X) ───
  // Mode switches and scroll events already clear fullscreen state via
  // editor.setMode() and saveScrollPosition(). This only handles ESC/X.
  useEffect(() => {
    return () => {
      if (registry.consumeUserCloseFlag()) {
        docViewState.clearFullscreen(docIdRef.current);
      }
    };
  }, [registry, docViewState]);

  // ─── Tab context menu ───
  useEffect(() => {
    const handle = (e: Event) => {
      setTabContextMenu((e as CustomEvent<typeof tabContextMenu>).detail);
    };
    const dismiss = () => setTabContextMenu(null);

    window.addEventListener('streamlain:database-tab-context-menu', handle);
    window.addEventListener('click', dismiss);
    return () => {
      window.removeEventListener(
        'streamlain:database-tab-context-menu',
        handle
      );
      window.removeEventListener('click', dismiss);
    };
  }, []);

  // ─── Tab reorder persistence ───
  useEffect(() => {
    const handle = (e: Event) => {
      const newOrder = (e as CustomEvent<{ tabs: string[] }>).detail.tabs;
      workspaceService.workspace.docCollection.meta.setDocMeta(docId, {
        databaseTabOrder: newOrder,
      } as Record<string, unknown>);
      setSavedTabOrder(newOrder);
    };

    window.addEventListener('database-tabs-reorder', handle);
    return () => window.removeEventListener('database-tabs-reorder', handle);
  }, [workspaceService, docId]);

  // ─── Custom theme body class override ───
  useEffect(() => {
    const cls = 'database-fullscreen-light-override';
    if (isCustomTheme) {
      document.body.classList.add(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [isCustomTheme]);

  // ─── Durable view sync (detect view changes from BlockSuite) ───
  useEffect(() => {
    const handle = (event: Event) => {
      const detail = (event as CustomEvent<{ blockId: string; viewId: string }>)
        .detail;
      if (detail.blockId === blockId) {
        setViewId(detail.viewId);
      }
    };

    window.addEventListener('streamlain:database-view-change', handle);
    return () => {
      window.removeEventListener('streamlain:database-view-change', handle);
    };
  }, [blockId]);

  // ─── Fullscreen database presence ───
  useEffect(() => {
    if (blockId === '__placeholder__') return;

    const awarenessStore = page.awarenessStore;
    const scope = `database:${page.id}:fullscreen`;
    awarenessStore.setLocalStateField('databaseFullscreenPresence', {
      scope,
      databaseId: blockId,
      updatedAt: Date.now(),
    });

    return () => {
      const current =
        awarenessStore.getLocalState()?.databaseFullscreenPresence;
      if (current?.scope !== scope || current.databaseId !== blockId) {
        return;
      }
      awarenessStore.setLocalStateField(
        'databaseFullscreenPresence',
        undefined
      );
    };
  }, [blockId, page]);

  // ─── Database tab switch: reuse registry.open to switch database ───
  useEffect(() => {
    const handle = (e: Event) => {
      const { databaseId } = (e as CustomEvent<{ databaseId: string }>).detail;
      if (databaseId) {
        registry.open('database', databaseId);
      }
    };

    window.addEventListener('database-tab-switch', handle);
    return () => window.removeEventListener('database-tab-switch', handle);
  }, [registry]);

  // ─── Database tab add: create new database on canvas ───
  useEffect(() => {
    const handle = () => {
      void (async () => {
        const editorContainer = editor.editorContainer$.value;
        if (!editorContainer?.host) return;

        try {
          const {
            databaseViewInitTemplate,
            provisionShardedDatabaseBlockDataSource,
          } = await import('@blocksuite/affine-block-database');
          const { serializeXYWH } = await import('@blocksuite/global/gfx');
          const { GfxControllerIdentifier } =
            await import('@blocksuite/std/gfx');
          const { EdgelessCRUDIdentifier } =
            await import('@blocksuite/affine-block-surface');

          const gfx = editorContainer.host.std.get(GfxControllerIdentifier);
          const crud = editorContainer.host.std.get(EdgelessCRUDIdentifier);

          const centerX = gfx.viewport.centerX;
          const centerY = gfx.viewport.centerY;
          const W = DEFAULT_CANVAS_DATABASE_WIDTH;
          const H = DEFAULT_CANVAS_DATABASE_HEIGHT;
          const provisioned = provisionShardedDatabaseBlockDataSource(gfx.doc, {
            title: 'Untitled Database',
            props: {
              xywh: serializeXYWH(centerX - W / 2, centerY - H / 2, W, H),
            },
            addBlock: (flavour, props, parentId) =>
              crud.addBlock(flavour, props, parentId),
            initDataSource: dataSource =>
              databaseViewInitTemplate(dataSource, 'table'),
          });
          registry.open('database', provisioned.databaseBlockId);
        } catch (error) {
          console.error('Failed to create database:', error);
        }
      })();
    };

    window.addEventListener('database-tab-add', handle);
    return () => window.removeEventListener('database-tab-add', handle);
  }, [editor, registry]);

  return (
    <>
      <div
        ref={fullscreenEditorHostRef}
        style={{
          display: 'flex',
          flex: '1 1 0',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {blockId !== '__placeholder__' && !blockAvailable ? null : (
          <LitDatabaseFullscreenEditor
            doc={page}
            databaseId={blockId}
            viewId={viewId}
            focusRowId={focusCellParams.rowId}
            focusRowShardDocId={focusCellParams.rowShardDocId}
            focusColumnId={focusCellParams.columnId}
            userId={currentAccount?.id ?? '__local__'}
            workspaceId={workspaceService.workspace.id}
            specs={fullscreenDatabaseSpecs}
            customTheme={isCustomTheme}
            tabOrder={savedTabOrder}
            style={{
              flex: '1 1 0',
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
              width: '100%',
            }}
          />
        )}
      </div>
      {/* Tab context menu portal */}
      {tabContextMenu &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              left: tabContextMenu.x,
              top: tabContextMenu.y,
              zIndex: 9999,
            }}
          >
            <Menu
              rootOptions={{
                open: true,
                onOpenChange: open => {
                  if (!open) setTabContextMenu(null);
                },
              }}
              items={
                <>
                  {!tabContextMenu.titleLoading ? (
                    <>
                      <MenuItem
                        prefixIcon={<EditIcon />}
                        onClick={() => {
                          window.dispatchEvent(
                            new CustomEvent('streamlain:database-tab-rename', {
                              detail: { tabId: tabContextMenu.tabId },
                            })
                          );
                          setTabContextMenu(null);
                        }}
                      >
                        Rename
                      </MenuItem>
                      <MenuSeparator />
                    </>
                  ) : null}
                  <MenuItem
                    prefixIcon={<DeleteIcon />}
                    type="danger"
                    onClick={() => {
                      const block = page.getBlock(tabContextMenu.tabId);
                      const protection = readManagedDatabaseProtectionFromModel(
                        block?.model
                      );
                      if (isManagedDatabaseProtected(protection)) {
                        setManagedDelete({
                          tabId: tabContextMenu.tabId,
                          tabTitle: tabContextMenu.tabTitle,
                          protection,
                        });
                        setTabContextMenu(null);
                        return;
                      }
                      openConfirmModal({
                        title: 'Delete Database',
                        description: `Are you sure you want to delete "${tabContextMenu.tabTitle}"? This action cannot be undone.`,
                        confirmText: 'Delete',
                        confirmButtonOptions: { variant: 'error' },
                        onConfirm: () => {
                          const block = page.getBlock(tabContextMenu.tabId);
                          if (block) {
                            page.deleteBlock(block.model);
                          }
                        },
                      });
                      setTabContextMenu(null);
                    }}
                  >
                    Delete
                  </MenuItem>
                </>
              }
            >
              <div style={{ width: 1, height: 1 }} />
            </Menu>
          </div>,
          document.body
        )}
      <ManagedDatabaseDeleteDialog
        open={managedDelete !== null}
        onOpenChange={open => {
          if (!open) setManagedDelete(null);
        }}
        actionLabel="Database löschen"
        databaseTitle={managedDelete?.tabTitle ?? ''}
        description="Diese systemverwaltete Database wird aus dem Dokument gelöscht."
        owner={
          managedDelete
            ? describeManagedDatabaseOwner({
                managedBy: managedDelete.protection.managedBy,
                fallbackDocTitle: 'Aktuelles Dokument',
                explicitOwner: resolveCustomAppDatabaseOwner({
                  blockId: managedDelete.tabId,
                  docId,
                  customApps,
                  openCustomApp: customAppId =>
                    workbenchService.workbench.open(
                      `/custom-apps/${customAppId}`,
                      { at: 'active' }
                    ),
                }),
                openSourceDoc: () =>
                  workbenchService.workbench.openDoc({
                    docId,
                    mode: 'edgeless',
                  }),
              })
            : null
        }
        onConfirm={() => {
          if (!managedDelete) return;
          const block = page.getBlock(managedDelete.tabId);
          if (block) {
            page.deleteBlock(block.model);
          }
          setManagedDelete(null);
        }}
      />
    </>
  );
};
